import type { AppConfig } from "../config";

/**
 * Sign out through Cognito's hosted UI.
 *
 * `auth.signoutRedirect()` does NOT work against Cognito, and the reason is
 * worth writing down because the endpoint looks standards-compliant:
 *
 * Cognito's discovery document advertises an `end_session_endpoint`, so
 * oidc-client-ts finds it and builds a spec-shaped request — `id_token_hint`
 * plus `post_logout_redirect_uri`. Cognito honours NEITHER. Its /logout wants
 * `client_id` + `logout_uri`, and given the standard parameters it redirects
 * to /login instead of signing anyone out.
 *
 * It gets worse, because oidc-client-ts almost compensates. OidcClient
 * appends `client_id` itself — but only `if (!client_id &&
 * post_logout_redirect_uri && !id_token_hint)`. react-oidc-context passes the
 * id token, so `id_token_hint` is set, the branch is skipped, and Cognito
 * receives no `client_id` at all:
 *
 *     /error?error=Client+does+not+exist
 *
 * So the URL is built by hand. Verified against the live dev pool:
 *   client_id + logout_uri        -> 302 to the console
 *   post_logout_redirect_uri only -> 302 to /login
 *
 * `logout_uri` must EXACTLY match a registered logout URL — AdminPlatformStack
 * registers `${origin}/`, trailing slash included.
 */
export function cognitoSignOutUrl(config: AppConfig, origin: string): string {
  const domain = config.hostedUiDomain.replace(/\/+$/, "");
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: `${origin.replace(/\/+$/, "")}/`,
  });
  return `${domain}/logout?${params.toString()}`;
}
